/**
 * AssistantMessage Component
 *
 * Renders assistant chat messages with advanced markdown support, intelligent JSON processing,
 * interactive elements, enhanced copy functionality, and visibly stopped partial output. Handles both streaming and complete
 * messages with smart content extraction and user feedback.
 *
 * Core Functionality:
 * - Renders markdown content using ReactMarkdown with GitHub Flavored Markdown
 * - Extracts and processes JSON from code blocks and inline objects
 * - Provides streaming-aware content filtering to prevent display artifacts
 * - Integrates UserChoice components for interactive action selections
 * - Features stateful copy-to-clipboard with visual feedback and temporary disable
 *
 * JSON Processing Logic:
 * - Detects JSON in ```json code blocks and inline {...} objects
 * - Separates action_options JSON for UserChoice rendering
 * - Displays regular JSON in formatted code blocks (only when not streaming)
 * - Filters incomplete/malformed JSON during streaming to maintain clean UI
 * - Validates JSON structure and handles parsing errors gracefully
 *
 * Streaming Behavior:
 * - Removes incomplete JSON code blocks at content end during streaming
 * - Hides regular JSON blocks during streaming to prevent partial display
 * - Processes complete action_options immediately for user interaction
 * - Cleans up extra whitespace and formatting artifacts
 *
 * Visual Layout:
 * - Modern winking assistant icon and borderless copy button column positioned to the left of the message bubble.
 * - Within the action buttons column, the icon is at the top, and the copy button is at the bottom. The column itself aligns with the height of the message content.
 * - Assistant icon features a friendly circular face with one normal eye, one winking eye, and a subtle smile.
 * - Small tech indicator dot at the top for modern AI assistant branding.
 * - Icon displayed above the copy button with subtle gray coloring.
 * - Message bubble with rounded corners and responsive width (max 80%).
 * - Dynamic button styling based on copy state (normal/copied).
 * - Dark mode support with theme-aware styling throughout.
 * - Proper spacing between content sections and action elements.
 *
 * State Management:
 * - Uses useState hook to track copy button state (isCopied)
 * - Implements 3-second timeout for automatic state reset
 * - Manages button appearance and behavior based on state
 *
 * Props:
 * - message: ChatMessage object containing assistant response content
 * - className: Optional CSS classes for additional styling
 * - onSubmitMessage: Callback function for UserChoice component interactions
 * - isStreaming: Boolean flag indicating if message is still being received
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useState } from 'react';
import { ChatMessage } from '../../models/ChatMessage';
import UserChoice from './UserChoice';

interface AssistantMessageProps {
  message: ChatMessage;
  className?: string;
  onSubmitMessage?: (message: string) => void;
  isStreaming?: boolean;
}

interface ActionOptionsJson {
  type: "action_options";
  text: string;
  options: string[];
}

/**
 * Extracts JSON content from a message and returns the content without JSON and extracted JSON blocks
 * Separates action_options JSON from regular JSON for different rendering
 * During streaming: processes complete action_options but hides incomplete/regular JSON
 */
const extractJsonFromContent = (content: string, isStreaming: boolean = false): {
  contentWithoutJson: string;
  jsonBlocks: string[];
  actionOptions: ActionOptionsJson[];
} => {
  const jsonBlocks: string[] = [];
  const actionOptions: ActionOptionsJson[] = [];
  let contentWithoutJson = content;

  // Extract JSON code blocks (```json...```)
  const jsonCodeBlockRegex = /```json\s*([\s\S]*?)\s*```/g;
  let match;

  while ((match = jsonCodeBlockRegex.exec(content)) !== null) {
    const jsonContent = match[1].trim();
    try {
      // Validate JSON
      const parsedJson = JSON.parse(jsonContent);

      // Check if it's action_options type
      if (parsedJson.type === "action_options" && parsedJson.text && Array.isArray(parsedJson.options)) {
        actionOptions.push(parsedJson as ActionOptionsJson);
        // Remove the JSON code block from content
        contentWithoutJson = contentWithoutJson.replace(match[0], '');
      } else if (!isStreaming) {
        // Only show regular JSON blocks when not streaming
        jsonBlocks.push(jsonContent);
        // Remove the JSON code block from content
        contentWithoutJson = contentWithoutJson.replace(match[0], '');
      } else {
        // When streaming, remove valid but non-action JSON blocks to prevent display
        contentWithoutJson = contentWithoutJson.replace(match[0], '');
      }
    } catch (e) {
      // When streaming, always remove incomplete/malformed JSON code blocks
      if (isStreaming) {
        contentWithoutJson = contentWithoutJson.replace(match[0], '');
      }
      // When not streaming, leave malformed JSON in content for user to see
    }
  }

  // During streaming, also remove any incomplete JSON code block at the end
  if (isStreaming) {
    // Remove incomplete JSON code blocks that might be at the end during streaming
    const incompleteJsonRegex = /```json\s*[\s\S]*$/;
    contentWithoutJson = contentWithoutJson.replace(incompleteJsonRegex, '');
  }

  // Extract inline JSON objects (basic detection for {...})
  const inlineJsonRegex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
  const inlineMatches = contentWithoutJson.match(inlineJsonRegex) || [];

  for (const inlineMatch of inlineMatches) {
    try {
      const parsedJson = JSON.parse(inlineMatch);

      // Check if it's action_options type
      if (parsedJson.type === "action_options" && parsedJson.text && Array.isArray(parsedJson.options)) {
        actionOptions.push(parsedJson as ActionOptionsJson);
        // Remove inline JSON from content
        contentWithoutJson = contentWithoutJson.replace(inlineMatch, '');
      } else if (!isStreaming) {
        // Only show regular JSON blocks when not streaming
        jsonBlocks.push(inlineMatch);
        // Remove inline JSON from content
        contentWithoutJson = contentWithoutJson.replace(inlineMatch, '');
      } else {
        // When streaming, remove valid but non-action JSON objects to prevent display
        contentWithoutJson = contentWithoutJson.replace(inlineMatch, '');
      }
    } catch (e) {
      // When streaming, remove incomplete JSON objects
      if (isStreaming) {
        contentWithoutJson = contentWithoutJson.replace(inlineMatch, '');
      }
      // When not streaming, leave malformed JSON in content
    }
  }

  // During streaming, remove any incomplete JSON objects at the end of content
  if (isStreaming) {
    // Remove incomplete JSON objects that might be forming at the end
    const incompleteJsonAtEndRegex = /\{[^{}]*$/;
    contentWithoutJson = contentWithoutJson.replace(incompleteJsonAtEndRegex, '');
  }

  // Clean up extra whitespace
  contentWithoutJson = contentWithoutJson.replace(/\n\s*\n\s*\n/g, '\n\n').trim();

  return { contentWithoutJson, jsonBlocks, actionOptions };
};

function AssistantMessage({ message, className = '', onSubmitMessage, isStreaming = false }: AssistantMessageProps) {
  const { contentWithoutJson, jsonBlocks, actionOptions } = extractJsonFromContent(message.content, isStreaming);
  const [isCopied, setIsCopied] = useState(false);
  const hasVisibleContent = Boolean(contentWithoutJson) || jsonBlocks.length > 0 || actionOptions.length > 0;

  // Copy message content to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      // Reset after 3 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Simple placeholder function for action options - to be implemented later
  const handleChoice = (_choice: string) => {
    // TODO: Implement actual choice handling
  };

  return (
    <div className={`flex justify-start items-stretch gap-1 ${className}`}> {/* Reduced gap from gap-3 to gap-2 */}
      {/* Action buttons column - Robot Icon & Copy - positioned on the left */}
      <div className="flex flex-col items-center justify-between pt-1"> {/* Added pt-1 for alignment with message text */}
        {/* Robot Assistant Icon */}
        <svg
          className="w-6 h-6 text-gray-500 dark:text-gray-400 mb-1"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M21.928 11.607c-.202-.488-.635-.605-.928-.633V8c0-1.103-.897-2-2-2h-6V4.61c.305-.274.5-.668.5-1.11a1.5 1.5 0 0 0-3 0c0 .442.195.836.5 1.11V6H5c-1.103 0-2 .897-2 2v2.997l-.082.006A1 1 0 0 0 1.99 12v2a1 1 0 0 0 1 1H3v5c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2v-5a1 1 0 0 0 1-1v-1.938a1.006 1.006 0 0 0-.072-.455zM5 20V8h14l.001 3.996L19 12v2l.001.005.001 5.995H5z" />
          <ellipse cx="8.5" cy="12" rx="1.5" ry="2" />
          <ellipse cx="15.5" cy="12" rx="1.5" ry="2" />
          <path d="M8 16h8v2H8z" />
        </svg>

        {/* Copy button - border removed */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCopy();
          }}
          disabled={isCopied}
          className={`p-2 rounded-md shadow-sm transition-colors ${isCopied
            ? 'bg-green-100 dark:bg-green-700 text-green-700 dark:text-green-200 cursor-not-allowed' // Adjusted colors for no border
            : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300' // Adjusted colors for no border
            }`}
          title={isCopied ? "Copied!" : "Copy message"}
        >
          {isCopied ? (
            <span className="text-xs font-medium px-1">Copied</span>
          ) : (
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Message content bubble */}
      <div className="rounded-lg px-4 py-2 max-w-[calc(80%-2rem)] bg-gray-100 dark:bg-gray-700"> {/* Adjusted max-width if needed due to icon column */}
        {/* Main message content */}
        {contentWithoutJson && (
          <div className="text-sm prose dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {contentWithoutJson}
            </ReactMarkdown>
          </div>
        )}

        {isStreaming && !hasVisibleContent && (
          <div className="flex items-center gap-1 py-1" aria-label="Assistant response in progress">
            <div className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        )}

        {message.isStopped && (
          <div className="mt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
            Stopped
          </div>
        )}

        {/* Extracted regular JSON blocks */}
        {jsonBlocks.map((jsonBlock, index) => (
          <div key={index} className="mt-3 first:mt-0">
            <div className="text-xs font-semibold mb-2 text-gray-500 dark:text-gray-400">
              JSON Response:
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
              <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(JSON.parse(jsonBlock), null, 2)}
              </pre>
            </div>
          </div>
        ))}

        {/* Action options as UserChoice components */}
        {actionOptions.map((actionOption, index) => (
          <div key={`action-${index}`} className="mt-3 first:mt-0">
            <UserChoice
              options={actionOption.options}
              onChoice={handleChoice}
              className=""
              onSubmitMessage={onSubmitMessage}
            />
          </div>
        ))}
      </div>

      {/* Old Action buttons - Copy - positioned outside on the right - REMOVED FROM HERE */}
      {/* 
      <div className="flex flex-col gap-1">
        // Copy button was here
      </div> 
      */}
    </div>
  );
}

export default AssistantMessage;
